// Debug panel — tweakable sliders for real-time parameter tuning
// Engine feature: toggle with ` (backtick). Game registers values.

export type SliderDef = {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
};

export class DebugPanel {
  private el: HTMLDivElement;
  private visible = false;
  private sliders: { def: SliderDef; input: HTMLInputElement; display: HTMLSpanElement }[] = [];

  constructor() {
    this.el = document.createElement("div");
    this.el.style.cssText =
      "position:fixed;top:30px;right:0;width:280px;background:rgba(28,30,36,0.92);" +
      "color:#8a8e9a;font:11px 'Consolas','SF Mono','Fira Code',monospace;padding:10px;z-index:10000;" +
      "display:none;max-height:90vh;overflow-y:auto;border-left:1px solid #2a2d35;";

    document.body.appendChild(this.el);

    window.addEventListener("keydown", (e) => {
      if (e.code === "Period") {
        this.visible = !this.visible;
        this.el.style.display = this.visible ? "block" : "none";
      }
    });
  }

  add(label: string, min: number, max: number, value: number, step: number, onChange: (v: number) => void): void {
    const row = document.createElement("div");
    row.style.cssText = "margin-bottom:6px;";

    const lbl = document.createElement("div");
    lbl.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px;";

    const nameSpan = document.createElement("span");
    nameSpan.textContent = label;

    const valSpan = document.createElement("span");
    valSpan.textContent = value.toFixed(3);
    valSpan.style.color = "#5c8cb4";

    lbl.appendChild(nameSpan);
    lbl.appendChild(valSpan);

    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);
    input.style.cssText = "width:100%;margin:0;accent-color:#5c8cb4;";

    input.addEventListener("input", () => {
      const v = parseFloat(input.value);
      valSpan.textContent = v.toFixed(3);
      onChange(v);
    });

    row.appendChild(lbl);
    row.appendChild(input);
    this.el.appendChild(row);

    this.sliders.push({ def: { label, min, max, step, value, onChange }, input, display: valSpan });
  }

  addSeparator(title: string): void {
    const sep = document.createElement("div");
    sep.style.cssText = "margin:10px 0 6px;padding-top:6px;border-top:1px solid #2a2d35;color:#3e4450;font-size:9px;text-transform:uppercase;letter-spacing:1.5px;";
    sep.textContent = title;
    this.el.appendChild(sep);
  }

  addColor(label: string, hex: string, onChange: (r: number, g: number, b: number) => void): void {
    const row = document.createElement("div");
    row.style.cssText = "margin-bottom:6px;";

    const lbl = document.createElement("div");
    lbl.style.cssText = "display:flex;justify-content:space-between;align-items:center;margin-bottom:2px;";

    const nameSpan = document.createElement("span");
    nameSpan.textContent = label;

    const input = document.createElement("input");
    input.type = "color";
    input.value = hex;
    input.style.cssText = "width:40px;height:20px;border:none;padding:0;cursor:pointer;background:none;";

    input.addEventListener("input", () => {
      const n = parseInt(input.value.slice(1), 16);
      onChange((n >> 16 & 0xff) / 255, (n >> 8 & 0xff) / 255, (n & 0xff) / 255);
    });

    lbl.appendChild(nameSpan);
    lbl.appendChild(input);
    row.appendChild(lbl);
    this.el.appendChild(row);
  }

  destroy(): void {
    this.el.remove();
  }
}
